import React, { useEffect, useMemo, useState } from 'react'

import Card from 'react-bootstrap/Card'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Stack from 'react-bootstrap/Stack'
import InputGroup from 'react-bootstrap/InputGroup'
import Dropdown from 'react-bootstrap/Dropdown'
import Select from 'react-select'

import AuthService from '../../../utilities/services/auth.service'
import {
  createDonationRule,
  getDonationRules,
  getPackages,
  getSeasons,
  getPackageMap,
  updateDonationRule,
} from '../../../utilities/api'
import { formatCurrency } from '../../../utilities/helpers'
import { LoadingContainer } from '../../LoadingContainer'
import { MoreIcon } from '../../MoreIcon'

import '../../BuyerTypesWrapper/buyerTypesWrapper.scss'
import './donations.scss'

const ADD_SELECT_STYLES = {
  container: (base) => ({
    ...base,
    display: 'inline-flex',
    flex: '0 0 auto',
    minWidth: '120px',
  }),
  control: (base) => ({
    ...base,
    minHeight: 'auto',
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#3e8bf7',
    fontWeight: 600,
    fontSize: '14px',
  }),
  valueContainer: (base) => ({ ...base, padding: 0 }),
  indicatorsContainer: () => ({ display: 'none' }),
  menu: (base) => ({ ...base, minWidth: '220px' }),
}

const EMPTY_RULE = {
  name: '',
  oneDonationCoversAll: true,
  minimumAmount: '100',
  scope: 'Per account, per year',
  givingLevels: ['100', '250', '500', '1000'],
  packageUUIDs: [],
  triggerSections: [],
}

function DragHandle() {
  return (
    <span className="donations-rule-modal__drag-handle" aria-hidden="true">
      <span />
      <span />
    </span>
  )
}

function CurrencyInput({ id, value, onChange, disabled = false }) {
  return (
    <InputGroup>
      <InputGroup.Text>$</InputGroup.Text>
      <Form.Control
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))}
      />
    </InputGroup>
  )
}

function extractSectionsFromPackageMap(response) {
  const sections =
    response?.data?.eventPackage?.seatmap?.mapping?.sections || {}
  return Object.values(sections)
    .map((section) => {
      const value = String(
        section?.sectionNumber || section?.sectionName || ''
      ).trim()
      if (!value) return null
      return {
        value,
        label: String(section?.sectionName || section?.sectionNumber || value),
      }
    })
    .filter(Boolean)
}

function SectionPickerBox({
  selectedItems,
  options,
  onAdd,
  onRemove,
  disabled,
  isLoading,
}) {
  const selectedValues = new Set(selectedItems.map((item) => item.value))
  const availableOptions = options.filter(
    (option) => !selectedValues.has(option.value)
  )

  return (
    <div className="donations-section-picker-box">
      {selectedItems.length > 0 && (
        <div className="donations-add-box donations-section-picker-box__selected">
          <div className="donations-add-box__inner">
            {selectedItems.map((item) => (
              <span key={item.value} className="donations-add-box__tag">
                {item.label}
                <button
                  type="button"
                  className="donations-add-box__remove"
                  onClick={() => onRemove(item)}
                  aria-label={`Remove ${item.label}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {disabled ? (
        <p className="donations-section-picker-box__hint text-muted mb-0">
          Select packages above to load sections from the venue seatmap.
        </p>
      ) : isLoading ? (
        <p className="donations-section-picker-box__hint text-muted mb-0">
          Loading seatmap sections...
        </p>
      ) : options.length === 0 ? (
        <p className="donations-section-picker-box__hint text-muted mb-0">
          No sections found on the venue seatmap.
        </p>
      ) : (
        <div
          className="donations-section-picker"
          role="listbox"
          aria-label="Venue seatmap sections"
        >
          {availableOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className="donations-section-picker__option"
              onClick={() => onAdd(option)}
              aria-label={`Add section ${option.label}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function AddItemBox({ items, options, onAdd, onRemove, addLabel, disabled }) {
  const availableOptions = options.filter(
    (option) => !items.some((item) => item.value === option.value)
  )

  return (
    <div className="donations-add-box">
      <div className="donations-add-box__inner">
        {items.map((item) => (
          <span key={item.value} className="donations-add-box__tag">
            {item.label}
            <button
              type="button"
              className="donations-add-box__remove"
              onClick={() => onRemove(item)}
              aria-label={`Remove ${item.label}`}
            >
              ×
            </button>
          </span>
        ))}
        {!disabled && availableOptions.length > 0 && (
          <Select
            options={availableOptions}
            value={null}
            onChange={(option) => {
              if (option) onAdd(option)
            }}
            placeholder={addLabel}
            styles={ADD_SELECT_STYLES}
            className="donations-add-box__select"
            classNamePrefix="react-select"
            isSearchable={availableOptions.length > 8}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
          />
        )}
        {disabled && (
          <span className="donations-add-box__add-disabled">{addLabel}</span>
        )}
      </div>
    </div>
  )
}

function TagPill({ label, variant = 'default' }) {
  return (
    <span className={`donations-tag donations-tag--${variant}`}>{label}</span>
  )
}

function getRuleSeasonLabel(rule, packageMetaByUUID) {
  if (rule.seasonLabel) return rule.seasonLabel

  const seasons = new Set()
  ;(rule.packageUUIDs || []).forEach((pkgUUID) => {
    const seasonLabel = packageMetaByUUID[pkgUUID]?.seasonLabel
    if (seasonLabel) seasons.add(seasonLabel)
  })

  if (seasons.size === 0) return null
  return [...seasons].join(', ')
}

function RuleActionsMenu({ rule, onEdit }) {
  return (
    <Dropdown
      className="btn-more w-max-content"
      drop="start"
      onClick={(event) => event.stopPropagation()}
    >
      <Dropdown.Toggle
        variant="default"
        id={`donation-rule-actions-${rule.id}`}
      >
        <MoreIcon />
      </Dropdown.Toggle>
      <Dropdown.Menu renderOnMount>
        <Stack as="ul" gap={2}>
          <li className="w-100">
            <Dropdown.Item
              as="button"
              className="icon-button-lg btn-edit"
              onClick={onEdit}
            >
              Edit
            </Dropdown.Item>
          </li>
        </Stack>
      </Dropdown.Menu>
    </Dropdown>
  )
}

function mapRuleToForm(rule, packageOptions) {
  const packageUUIDs = Array.isArray(rule.packageUUIDs) ? rule.packageUUIDs : []
  return {
    id: rule.id,
    name: rule.name || '',
    oneDonationCoversAll: rule.carryForward !== false,
    minimumAmount: String(rule.minimumAmount ?? ''),
    scope: rule.scopeLabel || 'Per account, per year',
    givingLevels: (rule.givingLevels || [rule.minimumAmount || 0]).map(String),
    packageUUIDs,
    triggerSections: rule.triggerSections || [],
    selectedPackages: packageOptions.filter((option) =>
      packageUUIDs.includes(option.value)
    ),
    selectedSections: (rule.triggerSections || []).map((section) => ({
      value: section,
      label: section,
    })),
  }
}

export default function Donations() {
  const organization = AuthService.getOrg()?.[0]
  const organizationUUID = organization?.uuid

  const [rules, setRules] = useState([])
  const [packageOptions, setPackageOptions] = useState([])
  const [sectionOptions, setSectionOptions] = useState([])
  const [isLoadingSections, setIsLoadingSections] = useState(false)
  const [selectedRuleId, setSelectedRuleId] = useState(null)
  const [ruleDetail, setRuleDetail] = useState(EMPTY_RULE)
  const [selectedPackages, setSelectedPackages] = useState([])
  const [selectedSections, setSelectedSections] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [showRuleModal, setShowRuleModal] = useState(false)

  const packageNameByUUID = useMemo(
    () =>
      packageOptions.reduce((acc, option) => {
        acc[option.value] = option.label
        return acc
      }, {}),
    [packageOptions]
  )

  const packageMetaByUUID = useMemo(
    () =>
      packageOptions.reduce((acc, option) => {
        acc[option.value] = option
        return acc
      }, {}),
    [packageOptions]
  )

  const loadRules = async () => {
    if (!organizationUUID) return
    const res = await getDonationRules(organizationUUID)
    setRules(res?.data?.data || [])
  }

  useEffect(() => {
    if (!organizationUUID) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    Promise.all([
      getDonationRules(organizationUUID),
      getSeasons(organizationUUID),
    ])
      .then(async ([rulesRes, seasonsRes]) => {
        if (cancelled) return
        const nextRules = rulesRes?.data?.data || []
        const seasons = seasonsRes?.data?.data || seasonsRes?.data || []
        const packageResponses = await Promise.all(
          seasons.map((season) =>
            getPackages({
              seasonId: Number(season.id),
              organizationUUID,
              page: 1,
              perPage: 200,
              status: 'all',
              query: '',
            }).catch(() => ({ data: { data: [] } }))
          )
        )
        const packages = packageResponses.flatMap(
          (response) => response?.data?.data || []
        )
        const uniquePackages = [
          ...new Map(packages.map((pkg) => [pkg.uuid, pkg])).values(),
        ]
        const options = uniquePackages.map((pkg) => ({
          value: pkg.uuid,
          label: pkg.name,
          seasonLabel: pkg.season?.name || null,
        }))
        setRules(nextRules)
        setPackageOptions(options)
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) setError('Failed to load donation rules.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!selectedPackages.length) {
      setSectionOptions([])
      setIsLoadingSections(false)
      return
    }

    let cancelled = false
    setIsLoadingSections(true)

    Promise.all(
      selectedPackages.map((pkg) =>
        getPackageMap(pkg.value, { includeSeatIdentity: false }).catch(
          () => null
        )
      )
    )
      .then((responses) => {
        if (cancelled) return
        const sectionMap = new Map()
        responses.forEach((response) => {
          extractSectionsFromPackageMap(response).forEach((section) => {
            if (!sectionMap.has(section.value)) {
              sectionMap.set(section.value, section)
            }
          })
        })
        const sorted = [...sectionMap.values()].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
        setSectionOptions(sorted)
      })
      .catch(() => {
        if (!cancelled) setSectionOptions([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSections(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedPackages])

  const handleSelectRule = (rule) => {
    const mapped = mapRuleToForm(rule, packageOptions)
    setSelectedRuleId(mapped.id)
    setRuleDetail(mapped)
    setSelectedPackages(mapped.selectedPackages)
    setSelectedSections(mapped.selectedSections)
    setError('')
    setShowRuleModal(true)
  }

  const handleCloseRuleModal = () => {
    setShowRuleModal(false)
    setError('')
  }

  const handleRuleDetailChange = (field, value) => {
    setRuleDetail((prev) => ({ ...prev, [field]: value }))
  }

  const handleMinimumAmountChange = (value) => {
    setRuleDetail((prev) => ({
      ...prev,
      minimumAmount: value,
      givingLevels: prev.givingLevels.map((level, index) =>
        index === 0 ? value : level
      ),
    }))
  }

  const addPackage = (option) => {
    setSelectedPackages((prev) => [...prev, option])
  }

  const removePackage = (option) => {
    setSelectedPackages((prev) => {
      const next = prev.filter((item) => item.value !== option.value)
      if (!next.length) setSelectedSections([])
      return next
    })
  }

  const addSection = (option) => {
    setSelectedSections((prev) => [...prev, option])
  }

  const removeSection = (option) => {
    setSelectedSections((prev) =>
      prev.filter((item) => item.value !== option.value)
    )
  }

  const removeGivingLevel = (index) => {
    setRuleDetail((prev) => ({
      ...prev,
      givingLevels: prev.givingLevels.filter((_, idx) => idx !== index),
    }))
  }

  const addGivingLevel = () => {
    setRuleDetail((prev) => ({
      ...prev,
      givingLevels: [...prev.givingLevels, ''],
    }))
  }

  const updateGivingLevel = (index, value) => {
    setRuleDetail((prev) => ({
      ...prev,
      givingLevels: prev.givingLevels.map((level, idx) =>
        idx === index ? value : level
      ),
    }))
  }

  const buildPayload = () => ({
    organizationUUID,
    name: ruleDetail.name.trim(),
    packageUUIDs: selectedPackages.map((option) => option.value),
    triggerSections: selectedSections.map((option) => option.value),
    minimumAmount: Number(ruleDetail.minimumAmount) || 0,
    givingLevels: ruleDetail.givingLevels
      .map((level) => Number(level))
      .filter((level) => Number.isFinite(level) && level > 0),
    carryForward: ruleDetail.oneDonationCoversAll,
    scopeLabel: ruleDetail.scope,
    status: 'active',
  })

  const handleSaveRule = async () => {
    if (!organizationUUID || !ruleDetail.name.trim()) {
      setError('Rule name is required.')
      return
    }

    setIsSaving(true)
    setError('')
    try {
      const payload = buildPayload()
      if (selectedRuleId) {
        await updateDonationRule(selectedRuleId, payload)
      } else {
        const res = await createDonationRule(payload)
        const created = res?.data?.data
        if (created?.id) setSelectedRuleId(created.id)
      }
      await loadRules()
      setShowRuleModal(false)
    } catch (err) {
      console.error(err)
      setError('Could not save donation rule.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateRule = () => {
    setSelectedRuleId(null)
    setRuleDetail(EMPTY_RULE)
    setSelectedPackages([])
    setSelectedSections([])
    setError('')
    setShowRuleModal(true)
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  return (
    <Stack gap={4} id="donations">
      {error && !showRuleModal && <div className="text-danger">{error}</div>}

      <div className="buyer-types-custom">
        <div className="buyer-types-panel__toolbar buyer-types-panel__toolbar--custom">
          <h2 className="buyer-types-section-heading">Donation rules</h2>
          <Button
            type="button"
            variant="primary"
            className="rounded-pill"
            onClick={handleCreateRule}
          >
            + Add rule
          </Button>
        </div>

        <div className="buyer-types-table-wrap donations-table-wrap">
          <table className="buyer-types-table donations-table">
            <thead>
              <tr>
                <th scope="col">Rule name</th>
                <th scope="col">Sections</th>
                <th scope="col">Minimum</th>
                <th scope="col">Applies to</th>
                <th scope="col">Status</th>
                <th
                  scope="col"
                  className="buyer-types-table__actions"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={6} className="donations-table__empty">
                    No donation rules yet.
                  </td>
                </tr>
              ) : (
                rules.map((rule) => {
                  const seasonLabel = getRuleSeasonLabel(
                    rule,
                    packageMetaByUUID
                  )

                  return (
                    <tr key={rule.id}>
                      <td className="donations-table__name-cell">
                        <div className="donations-rule-name">{rule.name}</div>
                        {seasonLabel ? (
                          <div className="donations-rule-season">
                            {seasonLabel}
                          </div>
                        ) : null}
                      </td>
                      <td className="donations-table__sections-cell">
                        <div className="donations-tags donations-tags--sections">
                          {(rule.triggerSections || []).map((section) => (
                            <TagPill
                              key={section}
                              label={section}
                              variant="section"
                            />
                          ))}
                        </div>
                      </td>
                      <td className="donations-table__minimum">
                        {formatCurrency(rule.minimumAmount)}
                      </td>
                      <td className="donations-table__packages-cell">
                        <div className="donations-tags donations-tags--packages">
                          {(rule.packageUUIDs || []).map((pkgUUID) => (
                            <TagPill
                              key={pkgUUID}
                              label={packageNameByUUID[pkgUUID] || pkgUUID}
                              variant="package"
                            />
                          ))}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`donations-status ${
                            rule.status === 'inactive'
                              ? 'donations-status--inactive'
                              : ''
                          }`}
                        >
                          {rule.status === 'inactive' ? 'Inactive' : 'Active'}
                        </span>
                      </td>
                      <td className="buyer-types-table__actions">
                        <RuleActionsMenu
                          rule={rule}
                          onEdit={() => handleSelectRule(rule)}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="donations-footer">
          <span className="donations-footer__icon" aria-hidden>
            i
          </span>
          <p className="donations-footer__text">
            Per account, per year. When the year rolls over, the requirement
            reappears unsatisfied on the new season&apos;s order.
          </p>
        </div>
      </div>

      <Modal
        centered
        animation={false}
        fullscreen="md-down"
        show={showRuleModal}
        onHide={handleCloseRuleModal}
        backdrop="static"
        className="vjx-modal-2xl donations-rule-modal"
        scrollable
      >
        <Modal.Header
          closeButton
          className="donations-rule-modal__header border-0 pb-0"
        >
          <div>
            <p className="donations-rule-modal__eyebrow text-upper text-upper-caption text-primary mb-2">
              {selectedRuleId ? 'Edit rule' : 'New rule'}
            </p>
            <Modal.Title className="h4 text-dark mb-0">
              {selectedRuleId ? 'Edit donation rule' : 'Create donation rule'}
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body className="donations-rule-modal__body pb-0">
          {error && <div className="text-danger mb-3">{error}</div>}

          <Form className="form-group donations-rule-modal__form">
            <Form.Group className="form-group" controlId="rule-name">
              <Form.Label>Rule name</Form.Label>
              <Form.Control
                value={ruleDetail.name}
                placeholder='e.g. "Henson Fund"'
                onChange={(e) => handleRuleDetailChange('name', e.target.value)}
              />
              <Form.Text>
                Shown on invoices, receipts, profile cards, and reports.
              </Form.Text>
            </Form.Group>

            <Form.Group className="form-group" controlId="rule-packages">
              <Form.Label>Applies to (packages)</Form.Label>
              <AddItemBox
                items={selectedPackages}
                options={packageOptions}
                onAdd={addPackage}
                onRemove={removePackage}
                addLabel="+ add package"
              />
              <Form.Text>
                Select one or more packages. A single rule can cover football +
                basketball together.
              </Form.Text>
            </Form.Group>

            <Card
              body
              className="donations-rule-modal__switch-card card--sm card-with-border h-auto"
            >
              <div className="donations-rule-modal__switch-row">
                <div>
                  <div className="normal-bold">
                    One donation covers all packages above
                  </div>
                  <small className="subtitle subtitle--dark mb-0">
                    If a donor satisfies this on their football renewal, it
                    carries forward — they won&apos;t be asked again on
                    basketball renewal.
                  </small>
                </div>
                <Form.Check
                  type="switch"
                  id="donation-covers-all-packages"
                  checked={ruleDetail.oneDonationCoversAll}
                  onChange={(e) =>
                    handleRuleDetailChange(
                      'oneDonationCoversAll',
                      e.target.checked
                    )
                  }
                  className="donations-rule-modal__switch"
                />
              </div>
            </Card>

            <Form.Group className="form-group" controlId="rule-sections">
              <Form.Label>Trigger sections</Form.Label>
              <SectionPickerBox
                selectedItems={selectedSections}
                options={sectionOptions}
                onAdd={addSection}
                onRemove={removeSection}
                disabled={!selectedPackages.length}
                isLoading={isLoadingSections}
              />
              <Form.Text>
                Click sections from the venue seatmap to add them. Tickets sold
                in these sections will trigger the donation requirement.
              </Form.Text>
            </Form.Group>

            <Row className="form-group g-4">
              <Col md={6}>
                <Form.Group
                  className="form-group mb-0"
                  controlId="minimum-amount"
                >
                  <Form.Label>Minimum amount</Form.Label>
                  <CurrencyInput
                    id="minimum-amount"
                    value={ruleDetail.minimumAmount}
                    onChange={handleMinimumAmountChange}
                  />
                  <Form.Text>
                    Cannot be zero. The first preset tier is locked to this
                    amount.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="form-group mb-0" controlId="scope">
                  <Form.Label>Scope</Form.Label>
                  <Form.Control
                    value={ruleDetail.scope}
                    readOnly
                    disabled
                    className="donations-rule-modal__scope-input"
                  />
                  <Form.Text>v1 supports per-account-per-year only.</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="form-group mb-0" controlId="giving-levels">
              <div className="donations-rule-modal__giving-levels">
                <div className="normal-bold mb-3">Preset giving levels</div>
                <Stack gap={2}>
                  {ruleDetail.givingLevels.map((level, index) => (
                    <Stack
                      key={`${index}-${level}`}
                      direction="horizontal"
                      gap={2}
                      className="donations-rule-modal__giving-level-row align-items-center"
                    >
                      <DragHandle />
                      <CurrencyInput
                        id={`giving-level-${index}`}
                        value={index === 0 ? ruleDetail.minimumAmount : level}
                        onChange={(value) => updateGivingLevel(index, value)}
                        disabled={index === 0}
                      />
                      {index === 0 ? (
                        <span className="donations-rule-modal__minimum-badge">
                          Minimum
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="donations-icon-btn"
                          onClick={() => removeGivingLevel(index)}
                          aria-label={`Remove giving level ${level}`}
                        >
                          ×
                        </button>
                      )}
                    </Stack>
                  ))}
                </Stack>
                <Button
                  type="button"
                  variant="link"
                  className="donations-rule-modal__add-link p-0 mt-3"
                  onClick={addGivingLevel}
                >
                  + Add giving level
                </Button>
              </div>
              <Form.Text>
                Custom amount is always available at checkout — these are just
                the quick-pick chips.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer className="donations-rule-modal__footer border-0 pt-0">
          <Button
            type="button"
            variant="link"
            className="donations-rule-modal__cancel"
            onClick={handleCloseRuleModal}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="donations-rule-modal__submit"
            disabled={isSaving || !ruleDetail.name.trim()}
            onClick={handleSaveRule}
          >
            {isSaving
              ? 'Saving...'
              : selectedRuleId
                ? 'Save rule'
                : 'Create rule'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Stack>
  )
}
